// client/src/pages/clips/ClipPage.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import VideoCardPlayer from "../../components/video/VideoCardPlayer";
import Leftbar from "../../components/LeftBar/lefbar";
import { STREAMERS } from "../../data/streams";
import {
  upsertClip,
  toggleLike,
  fetchComments,
  postComment,
  toggleCommentLike,
  deleteComment as apiDeleteComment,
} from "../../clipsApi";
import "./clippage.css";

/* ---------- Verified commenters: helpers ---------- */
const STREAMER_LOOKUP = (() => {
  const nameSet = new Set();
  const slugSet = new Set();
  for (const [slug, s] of Object.entries(STREAMERS)) {
    if (slug) slugSet.add(String(slug).toLowerCase());
    if (s?.displayName) nameSet.add(String(s.displayName).toLowerCase());
    if (s?.name) nameSet.add(String(s.name).toLowerCase());
  }
  return { nameSet, slugSet };
})();

function isCommentFromStreamer(raw, authorName) {
  const { nameSet, slugSet } = STREAMER_LOOKUP;
  const candidatesRaw = [
    raw?.user?.slug,
    raw?.author?.slug,
    raw?.user?.username,
    raw?.author?.username,
    raw?.username,
    raw?.displayName,
    raw?.name,
    raw?.authorName,
    authorName,
  ].filter(Boolean);

  const candidates = candidatesRaw
    .map((v) => String(v).trim())
    .flatMap((v) => [v, v.replace(/^@/, "")])
    .map((v) => v.toLowerCase());

  for (const v of candidates) {
    if (slugSet.has(v) || nameSet.has(v)) return true;
  }
  return false;
}

export default function ClipPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { streamerKey, vodId } = useParams();

  const streamer = STREAMERS[streamerKey] || {};
  const avatar = streamer.avatar || "/nullprofile.png";
  const displayName = streamer.displayName || streamerKey;

  const clip = useMemo(() => {
    const s = streamer;
    return (s.items || []).find((i) => String(i.vodId || "") === String(vodId || "")) || {};
  }, [streamer, vodId]);

  useEffect(() => {
    if (!clip?.playbackUrlVod) nav("/");
  }, [clip, nav]);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches
  );
  useEffect(() => {
    const mm = window.matchMedia("(max-width: 900px)");
    const onChange = (e) => setIsMobile(e.matches);
    mm.addEventListener?.("change", onChange);
    return () => mm.removeEventListener?.("change", onChange);
  }, []);

  const clipKey = `${streamerKey}:${vodId}`;
  const [counts, setCounts] = useState({ likesCount: 0, commentsCount: 0, liked: false });
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loadingComments, setLoadingComments] = useState(true);

  // reply state
  const [replyOpenId, setReplyOpenId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const setReplyDraft = (id, v) => setReplyDrafts((p) => ({ ...p, [id]: v }));

  /* ---- auth helpers ---- */
  const getCurrentUser = () => {
    try {
      const raw =
        localStorage.getItem("user") ||
        localStorage.getItem("currentUser") ||
        localStorage.getItem("profile");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const currentUser = getCurrentUser();
  const currentUserId =
    currentUser?.userId ||
    currentUser?._id ||
    currentUser?.id ||
    currentUser?.user?._id ||
    currentUser?.user?.id ||
    null;

  const isAuthed = !!(
    currentUser &&
    (currentUser._id || currentUser.id || currentUser.userId || currentUser.username || currentUser.email)
  );
  const loginUrl = `/login?next=${encodeURIComponent(loc.pathname + loc.search)}`;

  /* ---- normalize comments ---- */
  const cleanAuthorName = (v, fallback = "Anon") => {
    const s = (v ?? "").toString().trim();
    if (!s) return fallback;
    const parts = s
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (parts.length >= 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
      return parts[0];
    }
    return parts[0] || s || fallback;
  };

  const normalizeComment = (raw = {}) => {
    const candidate =
      raw.authorName ??
      raw.username ??
      raw.displayName ??
      raw.name ??
      raw.user?.username ??
      raw.user?.name ??
      raw.author?.username ??
      raw.author?.name ??
      (raw.isLocal && (currentUser?.username || currentUser?.name || currentUser?.displayName)) ??
      "Anon";

    const authorName = cleanAuthorName(candidate, "Anon");

    const body = raw.body ?? raw.text ?? raw.content ?? raw.message ?? "";

    // best-effort get author id (many backends use "_id" under user/author)
    const authorId =
      raw.userId ||
      raw.authorId ||
      raw?.user?._id ||
      raw?.user?.id ||
      raw?.author?._id ||
      raw?.author?.id ||
      null;

    return {
      _id: raw._id || raw.id || Math.random().toString(36).slice(2),
      authorId,
      authorName,
      body,
      parentId: raw.parentId || null,
      likesCount: typeof raw.likesCount === "number" ? raw.likesCount : 0,
      liked: !!raw.liked,
      createdAt: raw.createdAt || new Date().toISOString(),
      isVerified: isCommentFromStreamer(raw, authorName),
    };
  };

  // is own comment? prefer id, fall back to name match
  const isOwnComment = (c) => {
    if (!currentUser) return false;
    if (currentUserId && c.authorId) return String(currentUserId) === String(c.authorId);

    const myNames = [
      currentUser?.username,
      currentUser?.name,
      currentUser?.displayName,
      currentUser?.email,
      currentUser?.user?.username,
      currentUser?.user?.name,
    ]
      .filter(Boolean)
      .map((v) => cleanAuthorName(v))
      .map((v) => v.toLowerCase());

    return myNames.includes((c.authorName || "").toLowerCase());
  };

  // thread tree
  const threaded = useMemo(() => {
    const map = new Map();
    comments.forEach((c) => map.set(String(c._id), { ...c, replies: [] }));
    const roots = [];
    for (const c of map.values()) {
      if (c.parentId && map.has(String(c.parentId))) {
        map.get(String(c.parentId)).replies.push(c);
      } else {
        roots.push(c);
      }
    }
    return roots;
  }, [comments]);

  useEffect(() => {
    (async () => {
      try {
        const { clip: c, liked } = await upsertClip({
          clipKey,
          streamerKey,
          vodId,
          title: clip.title,
          playbackUrlVod: clip.playbackUrlVod,
          thumbnail: clip.thumbnail,
        });
        setCounts({
          likesCount: c?.likesCount || 0,
          commentsCount: c?.commentsCount || 0,
          liked: !!liked,
        });
      } catch {}
      try {
        setLoadingComments(true);
        const { comments: list, count } = await fetchComments(clipKey);
        const normalized = Array.isArray(list) ? list.map(normalizeComment) : [];
        setComments(normalized);
        setCounts((p) => ({ ...p, commentsCount: typeof count === "number" ? count : normalized.length }));
      } catch {
      } finally {
        setLoadingComments(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipKey, streamerKey, vodId, clip.title, clip.playbackUrlVod, clip.thumbnail]);

  const onToggleLike = async () => {
    setCounts((p) => ({
      ...p,
      liked: !p.liked,
      likesCount: Math.max(0, p.likesCount + (p.liked ? -1 : 1)),
    }));
    try {
      const { likesCount, liked } = await toggleLike(clipKey);
      setCounts((p) => ({ ...p, likesCount, liked }));
    } catch {}
  };

  /* ---- comment actions (auth-gated) ---- */
  const submitTopLevel = async (e) => {
    e.preventDefault();
    if (!isAuthed) return nav(loginUrl);
    const txt = text.trim();
    if (!txt) return;
    try {
      const resp = await postComment(clipKey, txt);
      const savedRaw = resp?.comment ?? resp ?? { text: txt };
      const saved = normalizeComment({ ...savedRaw, isLocal: true });
      setComments((c) => [saved, ...c]);
      setCounts((p) => ({ ...p, commentsCount: p.commentsCount + 1 }));
      setText("");
    } catch {}
  };

  const openReply = (id) => {
    if (!isAuthed) return nav(loginUrl);
    setReplyOpenId(id);
    setTimeout(() => {
      const el = document.getElementById(`reply-input-${id}`);
      el?.focus();
    }, 0);
  };

  const submitReply = async (e, parentId) => {
    e.preventDefault();
    if (!isAuthed) return nav(loginUrl);
    const body = (replyDrafts[parentId] || "").trim();
    if (!body) return;
    try {
      const resp = await postComment(clipKey, body, parentId);
      const savedRaw = resp?.comment ?? resp ?? { text: body, parentId };
      const saved = normalizeComment({ ...savedRaw, isLocal: true, parentId });
      setComments((c) => [saved, ...c]);
      setCounts((p) => ({ ...p, commentsCount: p.commentsCount + 1 }));
      setReplyDraft((d) => ({ ...d, [parentId]: "" }));
      setReplyOpenId(null);
    } catch {}
  };

  const onToggleCommentLike = async (commentId) => {
    if (!isAuthed) return nav(loginUrl);
    setComments((list) =>
      list.map((c) =>
        c._id === commentId ? { ...c, liked: !c.liked, likesCount: Math.max(0, c.likesCount + (c.liked ? -1 : 1)) } : c
      )
    );
    try {
      const { liked, likesCount } = await toggleCommentLike(clipKey, commentId);
      setComments((list) => list.map((c) => (c._id === commentId ? { ...c, liked, likesCount } : c)));
    } catch {}
  };

  // ---- Delete flow: three-dots menu on ALL devices ----
  const [deletingId, setDeletingId] = useState(null);

  // remove a comment and all descendants from flat list
  const removeCommentCascade = (list, id) => {
    const idStr = String(id);
    const childrenByParent = new Map();
    list.forEach((c) => {
      const p = c.parentId ? String(c.parentId) : null;
      if (!p) return;
      if (!childrenByParent.has(p)) childrenByParent.set(p, []);
      childrenByParent.get(p).push(String(c._id));
    });
    const toDelete = new Set([idStr]);
    const queue = [idStr];
    while (queue.length) {
      const cur = queue.shift();
      const kids = childrenByParent.get(cur) || [];
      for (const k of kids) {
        if (!toDelete.has(k)) {
          toDelete.add(k);
          queue.push(k);
        }
      }
    }
    return { nextList: list.filter((c) => !toDelete.has(String(c._id))), removedCount: toDelete.size };
  };

  // delete AFTER API succeeds (no optimistic removal)
  const deleteNow = async (comment) => {
    if (!isAuthed) return nav(loginUrl);
    if (!comment) return;
    setDeletingId(comment._id);
    try {
      await apiDeleteComment(clipKey, comment._id);
      setComments((prev) => {
        const { nextList, removedCount } = removeCommentCascade(prev, comment._id);
        setCounts((p) => ({ ...p, commentsCount: Math.max(0, p.commentsCount - removedCount) }));
        return nextList;
      });
    } catch (e) {
      alert(e?.message || "Failed to delete comment.");
    } finally {
      setDeletingId(null);
    }
  };

  const shareUrl = `${window.location.origin}/${streamerKey}/${vodId}`;
  const shareText = clip?.title || "";

  /* ---- SMART BACK ---- */
  const goBack = () => {
    const backTo = loc.state?.backTo;
    if (backTo) return nav(backTo, { replace: true });
    if (window.history.length > 1) return nav(-1);
    nav(`/${streamerKey}?tab=Lives`, { replace: true });
  };

  /* ---------- SEEK TO PRESERVED TIME ---------- */
  const videoWrapRef = useRef(null);
  const startAtState = Number(loc.state?.startAt);
  const startAtQuery = Number(new URLSearchParams(loc.search).get("t"));
  const startAtLocal = Number(localStorage.getItem(`yzy_seek_${clipKey}`));
  const startAt =
    [startAtState, startAtQuery, startAtLocal].find((v) => Number.isFinite(v) && v > 0) || 0;

  useEffect(() => {
    if (!startAt) return;
    const el = videoWrapRef.current?.querySelector("video");
    if (!el) return;

    const apply = () => {
      try {
        el.currentTime = startAt;
      } catch {}
      try {
        localStorage.removeItem(`yzy_seek_${clipKey}`);
      } catch {}
    };

    if (el.readyState >= 1) {
      apply();
    } else {
      const onMeta = () => {
        apply();
        el.removeEventListener("loadedmetadata", onMeta);
      };
      el.addEventListener("loadedmetadata", onMeta);
    }
  }, [startAt, clipKey, clip.playbackUrlVod]);

  return (
    <div style={pageStyle}>
      {/* 🔕 Kill any in-player spinners within this page's video containers only */}
      <style>{`
        .clip-video [class*="spinner"],
        .clip-video [class*="Spinner"],
        .clip-video [class*="loading"],
        .clip-video [class*="Loading"],
        .clip-video [class*="loader"],
        .clip-video [class*="Loader"],
        .clip-video [data-role="spinner"],
        .clip-video [aria-busy="true"],
        .clip-video .video-spinner,
        .clip-video .loading-overlay,
        .clip-video .buffering,
        .clip-video .plyr__spinner {
          display: none !important;
          animation: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `}</style>

      {/* top bar */}
      <header style={headerBar}>
        <button onClick={goBack} aria-label="Back" style={headerBackBtn}>
          <ArrowLeftIcon />
        </button>
      </header>

      {/* DESKTOP: leftbar | video | comments  —  MOBILE: video then comments */}
      {isMobile ? (
        <div style={mobileStack}>
          {/* VIDEO + meta/actions (in normal flow) */}
          <div className="clip-video" style={videoBox} ref={videoWrapRef}>
            <VideoCardPlayer
              src={clip.playbackUrlVod}
              poster={clip.thumbnail}
              autoPlay
              loop
              fit="cover"
              showSpinner={false}
              spinner={false}
              hideSpinner
              loadingIndicator={null}
              preload="auto"
            />
          </div>

          <div style={{ background: "#fff" }}>
            <div style={titleRow}>
              <a
                href={`/${streamerKey}`}
                style={{ display: "inline-block" }}
                aria-label={`Go to ${displayName}`}
                title={`Go to ${displayName}`}
              >
                <img src={avatar} alt="" style={avatarStyle} />
              </a>
              <div>
                {/* Streamer name + blue check */}
                <div style={{ display: "flex", alignItems: "center" }}>
                  <a
                    href={`/${streamerKey}`}
                    style={{ ...streamerName, textDecoration: "none", color: "#111", fontWeight: 600 }}
                    aria-label={`Go to ${displayName}`}
                    title={`Go to ${displayName}`}
                  >
                    {displayName}
                  </a>
                  <VerifiedCheck size={13} ml={6} />
                </div>

                {clip?.title && <h2 style={titleStyle}>{clip.title}</h2>}
              </div>
            </div>

            <div style={actionsRow}>
              <button onClick={onToggleLike} style={chipBtn} aria-label={counts.liked ? "Unlike" : "Like"}>
                <span style={{ lineHeight: 0, display: "grid", placeItems: "center" }}>
                  {counts.liked ? "♥" : "♡"}
                </span>
                <span>{counts.likesCount}</span>
              </button>

              <div style={chip}>
                <span>{counts.commentsCount}</span>
                <span>comments</span>
              </div>

              <ShareButton title={clip?.title} text={shareText} url={shareUrl} style={shareBtnWide}>
                SHARE THIS STREAM
              </ShareButton>
            </div>
          </div>

          {/* COMMENTS (mobile): page scrolls; composer sticks below header */}
          <aside style={commentsBlock}>
            <form onSubmit={submitTopLevel} style={composerStickyMobile}>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={isAuthed ? "ADD A COMMENT…" : "SIGN IN TO COMMENT"}
                style={composerInput}
                readOnly={!isAuthed}
                onFocus={() => {
                  if (!isAuthed) nav(loginUrl);
                }}
              />
              <button
                type={isAuthed ? "submit" : "button"}
                onClick={() => {
                  if (!isAuthed) nav(loginUrl);
                }}
                style={sendBtn}
                aria-disabled={!isAuthed}
              >
                {isAuthed ? "Send" : "Sign in"}
              </button>
            </form>

            {loadingComments ? (
              <div style={spinnerWrap}>
                <div className="clippage-spinner" style={spinner} />
              </div>
            ) : threaded.length === 0 ? (
              <div style={emptyState}>BE THE FIRST TO COMMENT.</div>
            ) : (
              <ul style={list}>
                {threaded.map((c) => (
                  <CommentNode
                    key={c._id}
                    c={c}
                    isOwn={isOwnComment}
                    replyOpenId={replyOpenId}
                    replyDrafts={replyDrafts}
                    setReplyDraft={setReplyDraft}
                    openReply={openReply}
                    submitReply={submitReply}
                    onToggleCommentLike={onToggleCommentLike}
                    onDeleteNow={deleteNow}
                    deletingId={deletingId}
                  />
                ))}
              </ul>
            )}
          </aside>
        </div>
      ) : (
        <div style={desktopShell}>
          {/* LEFTBAR (desktop only) */}
          <aside style={leftCol}>
            <Leftbar />
          </aside>

          {/* VIDEO + meta/actions (desktop, page scroll handles it) */}
          <div style={videoCol}>
            <div className="clip-video" style={videoBox} ref={videoWrapRef}>
              <VideoCardPlayer
                src={clip.playbackUrlVod}
                poster={clip.thumbnail}
                autoPlay
                loop
                fit="cover"
                showSpinner={false}
                spinner={false}
                hideSpinner
                loadingIndicator={null}
                preload="auto"
              />
            </div>

            <div style={{ background: "#fff" }}>
              <div style={titleRow}>
                <a
                  href={`/${streamerKey}`}
                  style={{ display: "inline-block" }}
                  aria-label={`Go to ${displayName}`}
                  title={`Go to ${displayName}`}
                >
                  <img src={avatar} alt="" style={avatarStyle} />
                </a>
                <div>
                  {/* Streamer name + blue check */}
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <a
                      href={`/${streamerKey}`}
                      style={{ ...streamerName, textDecoration: "none", color: "#111", fontWeight: 600 }}
                      aria-label={`Go to ${displayName}`}
                      title={`Go to ${displayName}`}
                    >
                      {displayName}
                    </a>
                    <VerifiedCheck size={13} ml={6} />
                  </div>

                  {clip?.title && <h2 style={titleStyle}>{clip.title}</h2>}
                </div>
              </div>

              <div style={actionsRow}>
                <button onClick={onToggleLike} style={chipBtn} aria-label={counts.liked ? "Unlike" : "Like"}>
                  <span style={{ lineHeight: 0, display: "grid", placeItems: "center" }}>
                    {counts.liked ? "♥" : "♡"}
                  </span>
                  <span>{counts.likesCount}</span>
                </button>

                <div style={chip}>
                  <span>{counts.commentsCount}</span>
                  <span>comments</span>
                </div>

                <ShareButton title={clip?.title} text={shareText} url={shareUrl} style={shareBtnWide}>
                  SHARE THIS STREAM
                </ShareButton>
              </div>
            </div>
          </div>

          {/* COMMENTS (desktop): page scroll; composer sticky under header */}
          <aside style={commentsDesktop}>
            <div style={commentsPaneFlat}>
              <form onSubmit={submitTopLevel} style={composerStickyDesktop}>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={isAuthed ? "ADD A COMMENT…" : "SIGN IN TO COMMENT"}
                  style={composerInput}
                  readOnly={!isAuthed}
                  onFocus={() => {
                    if (!isAuthed) nav(loginUrl);
                  }}
                />
                <button
                  type={isAuthed ? "submit" : "button"}
                  onClick={() => {
                    if (!isAuthed) nav(loginUrl);
                  }}
                  style={sendBtn}
                  aria-disabled={!isAuthed}
                >
                  {isAuthed ? "Send" : "Sign in"}
                </button>
              </form>

              {loadingComments ? (
                <div style={spinnerWrap}>
                  <div className="clippage-spinner" style={spinner} />
                </div>
              ) : threaded.length === 0 ? (
                <div style={emptyState}>BE THE FIRST TO COMMENT.</div>
              ) : (
                <ul style={list}>
                  {threaded.map((c) => (
                    <CommentNode
                      key={c._id}
                      c={c}
                      isOwn={isOwnComment}
                      replyOpenId={replyOpenId}
                      replyDrafts={replyDrafts}
                      setReplyDraft={setReplyDraft}
                      openReply={openReply}
                      submitReply={submitReply}
                      onToggleCommentLike={onToggleCommentLike}
                      onDeleteNow={deleteNow}
                      deletingId={deletingId}
                    />
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

/* ---------- Share Button ---------- */
function ShareButton({ title, text, url, style, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const nativeShare = async () => {
    try {
      if (navigator?.share) {
        const data = { title, text, url };
        if (!navigator.canShare || navigator.canShare(data)) {
          await navigator.share(data);
          return true;
        }
      }
    } catch {}
    return false;
  };

  const onClick = async () => {
    const usedNative = await nativeShare();
    if (!usedNative) setOpen((v) => !v);
  };

  useEffect(() => {
    const onAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("click", onAway);
    return () => document.removeEventListener("click", onAway);
  }, [open]);

  const tw = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(
    text || title || ""
  )}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(`${text || title || ""} ${url}`)}`;
  const tg = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text || title || "")}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const rd = `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title || "")}`;
  const sms = `sms:&body=${encodeURIComponent(`${text || title || ""} ${url}`)}`;
  const mail = `mailto:?subject=${encodeURIComponent(title || "")}&body=${encodeURIComponent(
    `${text || ""}\n\n${url}`
  )}`;

  return (
    <div ref={ref} style={{ position: "relative", flex: 1 }}>
      <button
        onClick={onClick}
        style={style || chipBtn}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-label="SHARE THIS STREAM"
      >
        <span style={{ lineHeight: 0, display: "grid", placeItems: "center" }}></span>
        <span>{children || "Share"}</span>
      </button>

      {open && (
        <div role="menu" style={shareMenu}>
          <a href={tw} target="_blank" rel="noopener noreferrer" style={shareLink}>
            Twitter / X
          </a>
          <a href={wa} target="_blank" rel="noopener noreferrer" style={shareLink}>
            WhatsApp
          </a>
          <a href={tg} target="_blank" rel="noopener noreferrer" style={shareLink}>
            Telegram
          </a>
          <a href={fb} target="_blank" rel="noopener noreferrer" style={shareLink}>
            Facebook
          </a>
          <a href={rd} target="_blank" rel="noopener noreferrer" style={shareLink}>
            Reddit
          </a>
          <a href={sms} style={shareLink}>
            Messages
          </a>
          <a href={mail} style={shareLink}>
            Email
          </a>
          <button
            type="button"
            style={shareLinkBtn}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(url);
              } catch {}
              setOpen(false);
            }}
          >
            Copy link
          </button>
        </div>
      )}
    </div>
  );
}

/* ----- threaded comment item ----- */
function CommentNode({
  c,
  isOwn,
  replyOpenId,
  replyDrafts,
  setReplyDraft,
  openReply,
  submitReply,
  onToggleCommentLike,
  onDeleteNow,
  deletingId,
}) {
  const own = isOwn(c);

  // three-dots menu (shown on both mobile & desktop)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const onAway = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener("click", onAway);
    return () => document.removeEventListener("click", onAway);
  }, [menuOpen]);

  const isDeleting = deletingId === c._id;

  return (
    <li style={commentItem}>
      <div style={commentHeader}>
        <div style={commentAuthor}>
          {c.authorName}
          {c.isVerified && <VerifiedCheck size={12} ml={6} />}
        </div>
        <div style={commentActions}>
          <button
            type="button"
            onClick={() => onToggleCommentLike(c._id)}
            style={commentActionBtn}
            aria-label={c.liked ? "Unlike" : "Like"}
            disabled={isDeleting}
          >
            <span style={{ marginRight: 6 }}>{c.liked ? "♥" : "♡"}</span>
            <span>{c.likesCount}</span>
          </button>
          <button type="button" onClick={() => openReply(c._id)} style={commentActionBtn} disabled={isDeleting}>
            Reply
          </button>

          {own && (
            <div style={{ position: "relative" }} ref={menuRef}>
              <button
                type="button"
                style={commentActionBtn}
                aria-haspopup="menu"
                aria-expanded={menuOpen ? "true" : "false"}
                onClick={() => setMenuOpen((v) => !v)}
                title="More"
                disabled={isDeleting}
              >
                <KebabIcon />
              </button>
              {menuOpen && (
                <div role="menu" style={deskMenu}>
                  <button
                    type="button"
                    style={deskMenuBtnDanger}
                    onClick={async () => {
                      setMenuOpen(false);
                      await onDeleteNow(c);
                    }}
                    disabled={isDeleting}
                    aria-busy={isDeleting ? "true" : "false"}
                  >
                    {isDeleting ? "Deleting…" : "Delete"}
                  </button>
                  <button type="button" style={deskMenuBtn} onClick={() => setMenuOpen(false)}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div>{c.body}</div>

      {replyOpenId === c._id && (
        <form onSubmit={(e) => submitReply(e, c._id)} style={replyComposerRow}>
          <input
            id={`reply-input-${c._id}`}
            value={replyDrafts[c._id] || ""}
            onChange={(e) => setReplyDraft(c._id, e.target.value)}
            placeholder="Write a reply…"
            style={replyInput}
            autoComplete="off"
            autoCorrect="on"
            autoCapitalize="sentences"
            disabled={isDeleting}
          />
          <button type="submit" style={replySendBtn} disabled={isDeleting}>
            Reply
          </button>
        </form>
      )}

      {c.replies?.length > 0 && (
        <ul style={childrenList}>
          {c.replies.map((r) => (
            <CommentNode
              key={r._id}
              c={r}
              isOwn={isOwn}
              replyOpenId={replyOpenId}
              replyDrafts={replyDrafts}
              setReplyDraft={setReplyDraft}
              openReply={openReply}
              submitReply={submitReply}
              onToggleCommentLike={onToggleCommentLike}
              onDeleteNow={onDeleteNow}
              deletingId={deletingId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/* ---------- Icons ---------- */
function ArrowLeftIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function KebabIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

/* Small blue verified check (Twitter-like) */
function VerifiedCheck({ size = 13, ml = 6 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ display: "inline-block", marginLeft: ml, flex: "0 0 auto" }}
    >
      <circle cx="12" cy="12" r="12" fill="#1DA1F2" />
      <path
        d="M17 9l-6.2 6L7 11.5"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ----- tiny css injector for spinner keyframes (COMMENTS ONLY) ----- */
function ensureStyle(id, css) {
  if (typeof document === "undefined") return;
  let tag = document.getElementById(id);
  if (!tag) {
    tag = document.createElement("style");
    tag.id = id;
    document.head.appendChild(tag);
  }
  if (!tag.textContent.includes(css.trim())) tag.textContent += css;
}
ensureStyle(
  "clippage-spinner-css",
  `
@keyframes clip-spin { to { transform: rotate(360deg); } }
`
);

/* ----- styles ----- */
const HEADER_H = 39;

/* page + header: WHOLE PAGE SCROLLS */
const pageStyle = {
  height: "100vh",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  background: "#fff",
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
};

const headerBar = {
  position: "sticky",
  top: 0,
  zIndex: 20,
  height: HEADER_H,
  display: "flex",
  alignItems: "center",
  background: "#fff",
  borderBottom: "1px solid #eee",
  padding: "0 8px",
};
const headerBackBtn = {
  width: 40,
  height: 40,
  borderRadius: 20,
  backgroundColor: "transparent",
  display: "grid",
  color: "black",
  placeItems: "center",
  cursor: "pointer",
  border: "1px solid transparent",
};

/* DESKTOP SHELL: leftbar | video | comments — page scrolls, no nested scroll */
const desktopShell = {
  display: "grid",
  gridTemplateColumns: "200px minmax(0,1fr) 420px",
  gap: 16,
  padding: 12,
};

const leftCol = {
  minWidth: 0,
  borderRight: "1px solid #eee",
  paddingRight: 12,
  overflow: "hidden",
};

const videoCol = { minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" };

const mobileStack = {
  display: "block",
  paddingBottom: 24,
};

const videoBox = { width: "100%", aspectRatio: "16 / 9", background: "#000", position: "relative" };

const titleRow = { display: "flex", alignItems: "center", gap: 10, padding: "10px 12px 0", backgroundColor: "#fff" };
const avatarStyle = { width: 36, height: 36, borderRadius: "50%", objectFit: "cover" };
const streamerName = { fontSize: 12, color: "#666", marginBottom: 2 };
const titleStyle = { margin: 0, fontSize: 18, lineHeight: 1.25, fontWeight: 600 };

const actionsRow = {
  display: "flex",
  gap: 12,
  padding: "10px 12px",
  alignItems: "center",
  background: "#fff",
};
const chipBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 999,
  border: "none",
  boxShadow: "none",
  background: "transparent",
  fontSize: 14,
  color: "#111",
};
const chip = { ...chipBase };
const chipBtn = { ...chipBase, cursor: "pointer" };

/* Wide, prominent share button */
const shareBtnWide = {
  ...chipBase,
  flex: 1,
  justifyContent: "center",
  gap: 10,
  padding: "12px 16px",
  border: "1px solid #111",
  background: "#111",
  color: "#fff",
  borderRadius: 14,
  fontWeight: 600,
  letterSpacing: "0.2px",
};

/* COMMENTS */
const commentsDesktop = {
  minWidth: 0,
  minHeight: 0,
  borderLeft: "1px solid #eee",
  padding: 0,
  background: "#fff",
};

const commentsPaneFlat = {
  background: "#fff",
};

/* block wrapper (mobile and desktop share) */
const commentsBlock = {
  background: "#fff",
  borderTop: "1px solid #eee",
};

/* sticky composer under header (works because page scrolls) */
const composerStickyBase = {
  position: "sticky",
  zIndex: 2,
  background: "#fff",
  padding: "8px 12px",
  display: "flex",
  gap: 8,
  borderBottom: "1px solid #eee",
};
const composerStickyMobile = { ...composerStickyBase, top: HEADER_H };
const composerStickyDesktop = { ...composerStickyBase, top: HEADER_H };

const composerInput = {
  flex: 1,
  border: "1px solid #ddd",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 16,
};
const sendBtn = {
  border: "1px solid #111",
  background: "#111",
  color: "#fff",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 14,
  cursor: "pointer",
};

const list = { listStyle: "none", margin: 0, padding: 12, display: "grid", gap: 12 };
const commentItem = { borderBottom: "1px solid #f1f1f1", paddingBottom: 12 };
const commentHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 };
const commentAuthor = { fontWeight: 600, fontSize: 14 };
const commentActions = { display: "flex", gap: 8, alignItems: "center" };
const commentActionBtn = { border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: "#333" };

const replyComposerRow = { display: "flex", gap: 8, marginTop: 8 };
const replyInput = { flex: 1, border: "1px solid #ddd", borderRadius: 12, padding: "8px 10px", fontSize: 14 };
const replySendBtn = { border: "1px solid #111", background: "#111", color: "#fff", borderRadius: 12, padding: "8px 10px", fontSize: 13, cursor: "pointer" };
const childrenList = { listStyle: "none", margin: "8px 0 0 12px", padding: 0, display: "grid", gap: 10 };

const emptyState = { color: "#777", padding: "16px 12px" };
const spinnerWrap = { display: "grid", placeItems: "center", padding: "16px 0" };
const spinner = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: "2px solid rgba(0,0,0,.15)",
  borderTopColor: "#111",
  animation: "clip-spin 0.8s linear infinite",
  willChange: "transform",
};

/* share menu styles */
const shareMenu = {
  position: "absolute",
  top: "110%",
  right: 0,
  background: "#fff",
  border: "1px solid #eee",
  borderRadius: 12,
  boxShadow: "0 6px 18px rgba(0,0,0,.08)",
  padding: 8,
  display: "grid",
  gap: 4,
  zIndex: 50,
  minWidth: 190,
};
const shareLink = {
  textDecoration: "none",
  fontSize: 14,
  color: "#111",
  padding: "8px 10px",
  borderRadius: 8,
  display: "block",
  lineHeight: 1.2,
};
const shareLinkBtn = {
  fontSize: 14,
  color: "#111",
  padding: "8px 10px",
  borderRadius: 8,
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  lineHeight: 1.2,
};

/* Desktop/mobile kebab dropdown (shared) */
const deskMenu = {
  position: "absolute",
  top: "120%",
  right: 0,
  background: "#fff",
  border: "1px solid #eee",
  borderRadius: 10,
  boxShadow: "0 8px 20px rgba(0,0,0,.08)",
  padding: 6,
  display: "grid",
  gap: 4,
  zIndex: 60,
  minWidth: 130,
};
const deskMenuBtn = {
  background: "transparent",
  border: "none",
  padding: "8px 10px",
  textAlign: "left",
  borderRadius: 8,
  cursor: "pointer",
};
const deskMenuBtnDanger = { ...deskMenuBtn, color: "#d30" };
